"use client";

import Link from "next/link";

import GalleryRasterImage from "@/components/media/GalleryRasterImage";
import { toast } from "@/components/ui/use-toast";
import { getThumbnail } from "@/lib/utils";
import { formatServiceDisplayTitle } from "@/lib/utils/service-title";
import {
	getSubsequentServiceMediaRasterSources,
} from "@/lib/utils/service-media-image";
import type { Service } from "@/types";

const BASKET_TOAST_DURATION_MS = 10_000;

function getBasketToastImageProps(service: Service): {
	source: { displayUrl?: string | null; url?: string | null } | string;
	fallbackRasterSources: string[];
} | null {
	const media = service.media ?? [];
	const firstIndex = media.findIndex((item) => {
		if (!item || item.type?.toUpperCase() === "VIDEO") return false;
		return Boolean(item.displayUrl?.trim() || item.url?.trim());
	});
	const first = firstIndex >= 0 ? media[firstIndex] : undefined;
	if (first) {
		return {
			source: first,
			fallbackRasterSources: getSubsequentServiceMediaRasterSources(media, firstIndex),
		};
	}

	const thumb = getThumbnail(service.thumbnailImage);
	if (thumb) {
		return { source: thumb, fallbackRasterSources: [] };
	}

	return null;
}

export function showAddedToBasketToast(service: Service, packageTitle: string) {
	const imageProps = getBasketToastImageProps(service);

	const { dismiss } = toast({
		variant: "basket",
		duration: BASKET_TOAST_DURATION_MS,
		description: (
			<div className="flex w-full items-center gap-3 pr-8 pt-0.5">
				<div className="relative size-14 shrink-0 overflow-hidden rounded-lg bg-muted">
					{imageProps ? (
						<GalleryRasterImage
							source={imageProps.source}
							fallbackRasterSources={imageProps.fallbackRasterSources}
							alt=""
							loading="eager"
							className="size-full object-cover"
						/>
					) : (
						<div className="flex size-full items-center justify-center text-xs text-muted-foreground">
							—
						</div>
					)}
				</div>
				<div className="min-w-0 flex-1">
					<p className="truncate text-sm font-semibold leading-snug text-foreground">
						{formatServiceDisplayTitle(service.title)}
					</p>
					<p className="mt-0.5 truncate text-sm text-muted-foreground">{packageTitle}</p>
				</div>
				<Link
					href="/basket"
					className="shrink-0 rounded-full border border-[color:var(--glass-border)] bg-secondary/95 px-4 py-2 text-sm font-semibold text-secondary-foreground shadow-sm backdrop-blur-sm transition-opacity hover:opacity-90"
				>
					View basket
				</Link>
			</div>
		),
	});

	window.setTimeout(() => dismiss(), BASKET_TOAST_DURATION_MS);
}
